package com.ijoin.platform

import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime

private fun browserTimeZoneId(): String? =
    (js("Intl.DateTimeFormat().resolvedOptions().timeZone") as? String)?.takeIf { it.isNotBlank() }

private fun formatInTimeZoneJs(epochMillis: Double, timeZoneId: String): String? {
    return try {
        val result = js("""
            (function(millis, tz) {
                try {
                    var date = new Date(millis);
                    var formatter = new Intl.DateTimeFormat('en-CA', {
                        timeZone: tz,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                    });
                    var parts = formatter.formatToParts(date);
                    var get = function(type) {
                        var found = parts.find(function(p) { return p.type === type; });
                        return found ? found.value : '0';
                    };
                    return get('year') + '-' + get('month') + '-' + get('day') + 'T' + get('hour') + ':' + get('minute') + ':' + get('second');
                } catch (e) {
                    return null;
                }
            })
        """)
        val formatted = result(epochMillis, timeZoneId)
        formatted as? String
    } catch (e: Exception) {
        null
    }
}

private fun localDateTimeToEpochMillisJs(year: Int, month: Int, day: Int, hour: Int, minute: Int, second: Int, timeZoneId: String): Double? {
    return try {
        val result = js("""
            (function(y, mo, d, h, mi, s, tz) {
                try {
                    // Create a rough estimate assuming the local time is UTC
                    var roughDate = new Date(Date.UTC(y, mo - 1, d, h, mi, s));

                    // Format this date in the target timezone to find the offset
                    var formatter = new Intl.DateTimeFormat('en-CA', {
                        timeZone: tz,
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: false
                    });
                    var parts = formatter.formatToParts(roughDate);
                    var get = function(type) {
                        return parseInt(parts.find(function(p) { return p.type === type; }).value);
                    };

                    var tzHour = get('hour');
                    var tzMinute = get('minute');
                    var tzDay = get('day');
                    var tzMonth = get('month');

                    // Calculate offset in minutes
                    var hourDiff = tzHour - h;
                    var minuteDiff = tzMinute - mi;

                    // Handle day boundary
                    if (tzDay !== d || tzMonth !== mo) {
                        if (tzDay > d || tzMonth > mo) {
                            hourDiff += 24;
                        } else {
                            hourDiff -= 24;
                        }
                    }

                    var offsetMinutes = hourDiff * 60 + minuteDiff;

                    // Adjust the rough date by subtracting the offset
                    return roughDate.getTime() - offsetMinutes * 60 * 1000;
                } catch (e) {
                    return null;
                }
            })
        """)
        val millis = result(year, month, day, hour, minute, second, timeZoneId)
        millis as? Double
    } catch (e: Exception) {
        null
    }
}

private val overrideMap = mutableMapOf<String, String>()

private fun TimeZone.withOverride(originalId: String): TimeZone {
    if (originalId != id) {
        overrideMap[id] = originalId
    } else {
        overrideMap.remove(id)
    }
    return this
}

actual fun getLocalTimeZone(): TimeZone {
    val browserZone = browserTimeZoneId()
    return if (browserZone != null) {
        createTimeZone(browserZone)
    } else {
        TimeZone.currentSystemDefault()
    }
}

actual fun getAllTimeZones(): List<String> {
    return listOf(
        // UTC
        "UTC",

        // Americas - USA
        "America/New_York",        // Eastern Time
        "America/Chicago",         // Central Time
        "America/Denver",          // Mountain Time
        "America/Los_Angeles",     // Pacific Time
        "America/Anchorage",       // Alaska Time
        "America/Phoenix",         // Arizona (no DST)
        "America/Honolulu",        // Hawaii Time

        // Americas - Canada
        "America/Toronto",
        "America/Vancouver",
        "America/Halifax",
        "America/Winnipeg",

        // Americas - Latin America
        "America/Mexico_City",
        "America/Bogota",
        "America/Lima",
        "America/Santiago",
        "America/Buenos_Aires",
        "America/Sao_Paulo",

        // Europe - Western
        "Europe/London",
        "Europe/Dublin",
        "Europe/Lisbon",

        // Europe - Central
        "Europe/Paris",
        "Europe/Berlin",
        "Europe/Rome",
        "Europe/Madrid",
        "Europe/Amsterdam",
        "Europe/Brussels",
        "Europe/Vienna",
        "Europe/Prague",
        "Europe/Warsaw",

        // Europe - Eastern
        "Europe/Moscow",
        "Europe/Istanbul",
        "Europe/Athens",
        "Europe/Helsinki",
        "Europe/Kiev",
        "Europe/Bucharest",

        // Asia - Middle East
        "Asia/Dubai",
        "Asia/Riyadh",
        "Asia/Tehran",
        "Asia/Jerusalem",

        // Asia - South Asia
        "Asia/Karachi",
        "Asia/Kolkata",
        "Asia/Dhaka",

        // Asia - Southeast Asia
        "Asia/Bangkok",
        "Asia/Singapore",
        "Asia/Jakarta",
        "Asia/Manila",
        "Asia/Ho_Chi_Minh",

        // Asia - East Asia
        "Asia/Shanghai",
        "Asia/Hong_Kong",
        "Asia/Tokyo",
        "Asia/Seoul",
        "Asia/Taipei",

        // Pacific
        "Australia/Sydney",
        "Australia/Melbourne",
        "Australia/Brisbane",
        "Australia/Perth",
        "Pacific/Auckland",
        "Pacific/Fiji",

        // Africa
        "Africa/Cairo",
        "Africa/Johannesburg",
        "Africa/Lagos",
        "Africa/Nairobi"
    ).sorted()
}

actual fun createTimeZone(timeZoneId: String): TimeZone {
    val resolved = runCatching { TimeZone.of(timeZoneId) }.getOrNull()
    val base = resolved ?: TimeZone.currentSystemDefault()
    return base.withOverride(timeZoneId)
}

actual val TimeZone.displayId: String
    get() = overrideMap[id] ?: id

actual fun Instant.toLocalDateTimeInTimeZone(timeZoneId: String): LocalDateTime {
    val formatted = formatInTimeZoneJs(toEpochMilliseconds().toDouble(), timeZoneId)
    return if (formatted != null) {
        LocalDateTime.parse(formatted)
    } else {
        // Fallback to system default if JS fails
        toLocalDateTime(TimeZone.currentSystemDefault())
    }
}

actual fun LocalDateTime.toInstantInTimeZone(timeZoneId: String): Instant {
    val epochMillis = localDateTimeToEpochMillisJs(
        year, monthNumber, dayOfMonth, hour, minute, second, timeZoneId
    )
    return if (epochMillis != null) {
        Instant.fromEpochMilliseconds(epochMillis.toLong())
    } else {
        // Fallback to system default if JS fails
        toInstant(TimeZone.currentSystemDefault())
    }
}
